import { FC, RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { Popover, Stack, Typography } from '@mui/material';


interface InputSuggestionsProps {
  inputRef: RefObject<HTMLInputElement | null>;
  suggestions: string[];
  label?: string;
  showWhenNotEmpty?: boolean;
  onSelect: (suggestion: string) => void;
}

export const InputSuggestionsPopover: FC<InputSuggestionsProps> = (props) => {
  const { inputRef, suggestions, label, onSelect, showWhenNotEmpty = false } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);


  const handleSelect = useCallback((suggestion: string) => {
    onSelect(suggestion);
  }, [onSelect]);

  const handleFocus = useCallback(() => {
    if (inputRef.current) {
      setAnchorEl(inputRef.current);
    }
  }, [inputRef]);

  const handleBlur = useCallback(() => {
    setTimeout(() => setAnchorEl(null), 100);
  }, []);

  useEffect(() => {
    const input = inputRef.current;

    if (!input) return;

    input.addEventListener('focus', handleFocus);
    input.addEventListener('blur', handleBlur);

    return () => {
      input.removeEventListener('focus', handleFocus);
      input.removeEventListener('blur', handleBlur);
    };
  }, [handleBlur, handleFocus, inputRef]);


  const isOpen = useMemo(() => {
    const isEmpty = (inputRef?.current?.value?.trim()?.length || 0) === 0;
    const inputIsSet = Boolean(anchorEl);

    return inputIsSet && (isEmpty || showWhenNotEmpty) && suggestions.length > 0;
  },[inputRef, anchorEl, showWhenNotEmpty, suggestions.length]);

  return (
    <Popover
      open={isOpen}
      anchorEl={anchorEl}
      onClose={() => setAnchorEl(null)}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      disableAutoFocus
      disableEnforceFocus
      PaperProps={{
        elevation: 0,
        sx: {
          p: 2,
          mt: 1,
          width: anchorEl ? anchorEl.clientWidth : '100%',
          borderRadius: '10px',
          border: '1px solid #e2e8f0',
        },
      }}
    >
      <Stack spacing={1}>
        <Typography variant="body2" fontSize={12} fontWeight={500}>{label || 'Suggestions'}</Typography>
        {suggestions.map((suggestion, index) => (
          <Typography
            key={index}
            variant="body2"
            fontSize={14}
            color="text.primary"
            sx={{
              px: 1,
              py: 0.5,
              borderRadius: 1,
              '&:hover': {
                backgroundColor: 'action.hover',
                cursor: 'pointer',
              },
            }}
            onMouseDown={() => handleSelect(suggestion)}
          >
            {suggestion}
          </Typography>
        ))}
      </Stack>
    </Popover>
  );
};
